import React, { useState, useEffect, createContext, useContext } from "react";
import { isEqual } from "lodash";
import { landscapeRule, sizes } from "./breakpoints";

interface QueryMatch {
    xs: boolean;
    sm: boolean;
    md: boolean;
    lg: boolean;
    xl: boolean;
    xxsLandscape: boolean;
    xsLandscape: boolean;
    smLandscape: boolean;
    tbLandscape: boolean;
    mdLandscape: boolean;
    lgLandscape: boolean;
    xlLandscape: boolean;
    landscape: boolean;
}

const defaultValue: QueryMatch = {
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
    xxsLandscape: false,
    xsLandscape: false,
    smLandscape: false,
    tbLandscape: false,
    mdLandscape: false,
    lgLandscape: false,
    xlLandscape: false,
    landscape: false,
};

const defaultJsQueries: any = {
    xs: `(min-width: ${sizes.xs}px)`,
    sm: `(min-width: ${sizes.sm}px)`,
    tb: `(min-width: ${sizes.tb}px)`,
    md: `(min-width: ${sizes.md}px)`,
    lg: `(min-width: ${sizes.lg}px)`,
    xl: `(min-width: ${sizes.xl}px)`,
    landscape: landscapeRule,
};

defaultJsQueries.xsLandscape = `${defaultJsQueries.xs} and ( ${landscapeRule} )`;
defaultJsQueries.smLandscape = `${defaultJsQueries.sm} and ( ${landscapeRule} )`;
defaultJsQueries.tbLandscape = `${defaultJsQueries.tb} and ( ${landscapeRule} )`;
defaultJsQueries.mdLandscape = `${defaultJsQueries.md} and ( ${landscapeRule} )`;
defaultJsQueries.lgLandscape = `${defaultJsQueries.lg} and ( ${landscapeRule} )`;
defaultJsQueries.xlLandscape = `${defaultJsQueries.xl} and ( ${landscapeRule} )`;

const getMediaQueriesForJs = () => defaultJsQueries;

const BreakpointContext = createContext(defaultValue);

const BreakpointProvider = ({ children }: { children: any }) => {
    const queries = getMediaQueriesForJs();
    const [queryMatch, setQueryMatch] = useState<QueryMatch>(defaultValue);

    useEffect(() => {
        const mediaQueryLists: { [key: string]: any } = {};
        const keys = Object.keys(queries);
        let isAttached = false;

        const handleQueryListener = () => {
            const updatedMatches = keys.reduce((acc: any, media: any) => {
                acc[media] = !!(mediaQueryLists[media] && mediaQueryLists[media].matches);
                return acc;
            }, {});
            setQueryMatch(updatedMatches);
        };

        if (window && window.matchMedia) {
            const matches: QueryMatch = { ...defaultValue };
            const matchesAny: any = matches;
            keys.forEach(media => {
                if (typeof queries[media] === "string") {
                    mediaQueryLists[media] = window.matchMedia(queries[media]);
                    matchesAny[media] = mediaQueryLists[media].matches;
                } else {
                    matchesAny[media] = false;
                }
            });
            setQueryMatch(matches);
            isAttached = true;
            keys.forEach(media => {
                if (typeof queries[media] === "string") {
                    mediaQueryLists[media].addListener(handleQueryListener);
                }
            });
        }

        return () => {
            if (isAttached) {
                keys.forEach(media => {
                    if (typeof queries[media] === "string") {
                        mediaQueryLists[media].removeListener(handleQueryListener);
                    }
                });
            }
        };
    }, [queries]);

    return (
        <BreakpointContext.Provider value={queryMatch}>
            {isEqual(queryMatch, defaultValue) ? null : children}
        </BreakpointContext.Provider>
    );
};

function useBreakpoint() {
    return useContext(BreakpointContext);
}

export { useBreakpoint, BreakpointProvider };
